import { useRef, useEffect, useState, useMemo } from 'react';
import waveSurfer from 'wavesurfer.js';
import cm from 'classnames';

import { isObject } from 'helpers/_helpers';
import { MIN_REGION_DUR } from 'constants/params';
import { LogLevel } from 'services/logger.service';
import { ClipTransitionAlignment } from 'services/story.service';
import { useAppDispatch, useAppSelector } from 'store/store-hooks';
import { IntroKeys } from 'components/app/intro/intro-keys';
import { useLocation, Path } from 'components/_hooks/use-router';
import { EnumDragger } from 'components/app/_mediator/app.slice';
import { appLogger } from 'components/app/_mediator/app-logger.slice';
import { useDraggerContext } from 'components/app/_mediator/app-dragger.provider';
import { EnumFader, useSbPlayerCb } from 'components/app/storyboard/sb-player/_mediator/use-sb-player-cb';
import { resetAudioFile, setWaveIsReady, setDuration } from './_mediator/audio-track.slice';
import { useAudioTrackContext } from './_mediator/audio-track.provider';
import { useWsSeek } from './_mediator/use-ws-seek';
import { WS_PARAMS } from './lib/ws-params';

import { WsHeader } from 'components/app/audio-track/ws-header/ws-header';
import { AudioTrackHooks } from './audio-track-hooks';
import { WsTimeLabel } from './ws-time-label';
import styles from './_audio-track.module.scss';

export const AudioTrack = ({ className }: { className?: string }) => {
  const { path } = useLocation();
  const { dragger } = useDraggerContext();
  const { toggleFader } = useSbPlayerCb();
  const { ws, wsGetCursorFraction } = useAudioTrackContext();
  const { storyLoaded, heldKey } = useAppSelector((state) => state.app);
  const intro = useAppSelector((state) => state.app.intro);
  const transitionTime = useAppSelector((state) => state.editor.storyConfig?.transitionTime ?? null);
  const selectedMarkers = useAppSelector((state) => state.editor).selectedMarkers;
  const waveIsReady = useAppSelector((state) => state.audio.waveIsReady);
  const audioId = useAppSelector((state) => state.audio.audioId);
  const files = useAppSelector((state) => state.app__files.files);
  const clipTransitionAlignment = useAppSelector((state) =>
    path === Path.storyboard
      ? state.editor.storyConfig?.clipTransitionAlignment
      : state.fc__data.fcStoryConfig?.clipTransitionAlignment
  );
  const container = useRef<HTMLDivElement>(null);
  const wsAudioId = useRef<AudioFileId>();
  const dispatch = useAppDispatch();
  const seekTo = useWsSeek();

  const [isOver, setIsOver] = useState(false);

  // Determine which data-intro to use based on tutorial state
  const getDataIntro = () => {
    // When audio tutorial is running, highlight entire track; otherwise highlight only header
    return intro === IntroKeys.sbAudioHeader ? IntroKeys.sbAudioHeader : IntroKeys.sbAudio;
  };

  const scrollBarSize = useMemo(() => {
    return +styles.scrollBarSize.match(/(\d+)/)![0] + 2;
  }, []);

  const markerPositionStyle = useMemo(() => {
    if (clipTransitionAlignment === undefined) return;
    if (transitionTime !== null && transitionTime <= MIN_REGION_DUR) return styles['body--markersLeft'];
    switch (clipTransitionAlignment) {
      case ClipTransitionAlignment.Trailing:
        return styles['body--markersLeft'];
      case ClipTransitionAlignment.Centered:
        return styles['body--markersCenter'];
      case ClipTransitionAlignment.Leading:
        return styles['body--markersRight'];
    }
  }, [clipTransitionAlignment, transitionTime]);

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!container.current) return;
    if (heldKey) return;
    if (path === Path.storyboard) toggleFader(EnumFader.on);
    if (container.current.getBoundingClientRect().bottom - e.clientY < scrollBarSize) return;
    dragger(container, EnumDragger.ws).start(e, (e) => seekTo(wsGetCursorFraction(e.clientX)));
  };

  useEffect(() => {
    ws.current = waveSurfer.create({
      container: container.current!,
      ...WS_PARAMS,
    });
    ws.current.on('ready', () => {
      dispatch(setDuration(ws.current!.getDuration()));
      dispatch(setWaveIsReady(true));
    });
    ws.current.on('error', (message) => {
      if (isObject(message) && Object.keys(message).length === 0) return;
      const payload = {
        mediaFileId: audioId,
        fileName: files[audioId!].fileName,
        url: files[audioId!].fileWithEffectsUrls.fileUrl,
      };
      dispatch(
        appLogger({
          level: LogLevel.Fatal,
          error: { name: 'wavesurfer', message: `${message}`, stack: JSON.stringify(payload) },
        })
      );
    });

    return () => {
      ws.current!.cancelAjax();
      ws.current!.destroy();
      ws.current!.unAll();
      ws.current = undefined;
      dispatch(resetAudioFile());
    };
  }, []);

  useEffect(() => {
    if (!storyLoaded || !audioId || wsAudioId.current) return;
    ws.current!.load(files[audioId].fileWithEffectsUrls.fileUrl ?? '');
    wsAudioId.current = audioId;
  }, [storyLoaded, audioId && files[audioId]]);

  useEffect(() => {
    if (!audioId || !wsAudioId.current || wsAudioId.current === audioId) return;
    window.location.reload();
  }, [audioId]);

  return (
    <div className={cm(styles.root, className)} data-intro={getDataIntro()}>
      {waveIsReady && (
        <>
          <AudioTrackHooks ws={ws.current!} root={container.current!} />
          <WsHeader ws={ws.current!} />
          <WsTimeLabel ws={ws.current!} isOver={isOver} />
        </>
      )}
      <div
        ref={container}
        onPointerDown={onPointerDown}
        onPointerUp={(e) => dragger(container, EnumDragger.ws).end(e)}
        onMouseEnter={() => setIsOver(true)}
        onMouseLeave={() => setIsOver(false)}
        className={cm(styles.body, markerPositionStyle, {
          [styles['body--selectedMarkers']]: selectedMarkers.length,
          [styles['body--selectedMarkersFixed']]: heldKey,
        })}
      />
      <div id="timeline" />
    </div>
  );
};
